import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookText, Loader2, Download, FileSpreadsheet, ReceiptText, AlertTriangle,
  CheckCircle2, Clock, Mail, CalendarClock, Globe2, Send,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CierreRevision from "./CierreRevision";
import { CierreLineas } from "./CierreLineas";
import CierreBuscador from "./CierreBuscador";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); } catch { return iso; } };

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
    <div className={`flex h-8 w-8 items-center justify-center rounded-md ${tone}`}><Icon className="h-4 w-4" /></div>
    <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p><p className="text-base font-bold tabular-nums leading-none">{value}</p></div>
  </div>
);

export default function ContabilidadPage() {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lineasCierre, setLineasCierre] = useState(null);

  const previewQ = useQuery({ queryKey: ["conta-preview"], queryFn: async () => (await api.get("/contabilidad/preview")).data });
  const cierresQ = useQuery({ queryKey: ["conta-cierres"], queryFn: async () => (await api.get("/contabilidad/cierres")).data });

  const genMut = useMutation({
    mutationFn: async () => (await api.post("/contabilidad/cierres/generar")).data,
    onSuccess: (d) => {
      toast.success(`Cierre ${d.week_label} generado: ${d.counts.holded} Holded · ${d.counts.retencion} retención · ${d.counts.pendientes} pendientes`);
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ["conta-cierres"] });
      qc.invalidateQueries({ queryKey: ["conta-preview"] });
    },
    onError: (e) => { toast.error(apiErrorMessage(e, "No se pudo generar el cierre")); setConfirmOpen(false); },
  });

  const reenviarMut = useMutation({
    mutationFn: async (cid) => (await api.post(`/contabilidad/cierres/${cid}/reenviar-email`)).data,
    onSuccess: (d) => { toast.success(`Email enviado a ${d.email_destino} · ${d.facturas} factura(s)`); qc.invalidateQueries({ queryKey: ["conta-cierres"] }); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo enviar el email")),
  });

  const downloadZip = async (c) => {
    try {
      const res = await api.get(`/contabilidad/cierres/${c.id}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = c.zip_filename || `${c.week_label}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const pv = previewQ.data;
  const cierres = cierresQ.data?.items || [];

  return (
    <div className="space-y-5" data-testid="contabilidad-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <BookText className="h-6 w-6 text-primary" /> Contabilidad · Cierres semanales
          </h1>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" /> Cierre automático cada <strong>domingo 23:59</strong> (Madrid). También puedes generarlo manualmente.
          </p>
        </div>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <Button data-testid="conta-generar"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Generar cierre ahora</Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="conta-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>¿Generar el cierre de esta semana?</AlertDialogTitle>
              <AlertDialogDescription>
                Se generará el ZIP con el Excel Holded por país, las facturas con retención y los pendientes.
                Las facturas incluidas quedarán <strong>bloqueadas</strong> (no se podrán borrar ni sustituir).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); genMut.mutate(); }} disabled={genMut.isPending} data-testid="conta-confirm-ok">
                {genMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Generando…</> : "Generar cierre"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Tabs defaultValue="cierres" className="w-full">
        <TabsList data-testid="conta-tabs">
          <TabsTrigger value="cierres" data-testid="conta-tab-cierres">Cierres</TabsTrigger>
          <TabsTrigger value="revision" data-testid="conta-tab-revision">Revisión y edición</TabsTrigger>
          <TabsTrigger value="buscador" data-testid="conta-tab-buscador">Archivo / Buscador</TabsTrigger>
        </TabsList>
        <TabsContent value="cierres" className="mt-4 space-y-5">
      {/* Preview de la semana en curso */}
      <Card data-testid="conta-preview-card"><CardContent className="p-4">
        <p className="mb-3 text-sm font-semibold">Pendiente de cierre {pv?.week_label ? `· semana ${pv.week_label}` : ""}</p>
        {previewQ.isLoading ? <Skeleton className="h-14 w-full" /> : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat icon={FileSpreadsheet} label="Holded" value={pv?.holded ?? 0} tone="bg-emerald-100 text-emerald-700" />
            <Stat icon={ReceiptText} label="Con retención" value={pv?.retencion ?? 0} tone="bg-amber-100 text-amber-700" />
            <Stat icon={AlertTriangle} label="Pendientes" value={pv?.pendientes ?? 0} tone="bg-rose-100 text-rose-700" />
            <Stat icon={Clock} label="Proformas (excl.)" value={pv?.proformas_excluidas ?? 0} tone="bg-slate-100 text-slate-600" />
          </div>
        )}
      </CardContent></Card>

      {/* Historial de cierres */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground">Historial de cierres</h2>
        {cierresQ.isLoading ? <Skeleton className="h-24 w-full" /> : cierres.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="conta-empty">Aún no hay cierres. Genera el primero con "Generar cierre ahora".</CardContent></Card>
        ) : cierres.map((c) => (
          <Card key={c.id} data-testid={`conta-cierre-${c.id}`}><CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground">{c.week_label}</Badge>
                  <span className="text-xs text-muted-foreground">{fmtDay(c.desde)} – {fmtDay(c.hasta)} · generado {fmtDate(c.generated_at)} por {c.generated_by}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">{c.counts?.holded || 0} Holded</Badge>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">{c.counts?.retencion || 0} retención</Badge>
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-800">{c.counts?.pendientes || 0} pendientes</Badge>
                  {Object.keys(c.por_pais || {}).length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Globe2 className="h-3.5 w-3.5" />{Object.entries(c.por_pais).map(([p, v]) => `${p} (${v.facturas})`).join(" · ")}</span>
                  )}
                </div>
                {c.counts?.retencion > 0 && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      {c.email_enviado
                        ? <span className="text-emerald-700">Retenciones enviadas a {c.email_destino}</span>
                        : <span className="text-amber-700">Email retenciones pendiente: {c.email_detalle}</span>}
                    </span>
                    <Button
                      variant="ghost" size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => reenviarMut.mutate(c.id)}
                      disabled={reenviarMut.isPending && reenviarMut.variables === c.id}
                      data-testid={`conta-reenviar-${c.id}`}
                    >
                      {reenviarMut.isPending && reenviarMut.variables === c.id
                        ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Enviando…</>
                        : <><Send className="mr-1 h-3 w-3" />{c.email_enviado ? "Reenviar" : "Enviar ahora"}</>}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => setLineasCierre(c)} data-testid={`conta-lineas-${c.id}`}><FileSpreadsheet className="mr-1.5 h-4 w-4" />Ver/editar líneas</Button>
                <Button variant="outline" size="sm" onClick={() => downloadZip(c)} data-testid={`conta-zip-${c.id}`}><Download className="mr-1.5 h-4 w-4" />Descargar ZIP</Button>
              </div>
            </div>
          </CardContent></Card>
        ))}
      </div>
        </TabsContent>
        <TabsContent value="revision" className="mt-4">
          <CierreRevision />
        </TabsContent>
        <TabsContent value="buscador" className="mt-4">
          <CierreBuscador />
        </TabsContent>
      </Tabs>

      {lineasCierre && <CierreLineas cierre={lineasCierre} onClose={() => setLineasCierre(null)} />}
    </div>
  );
}
